import React, { useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { saveAs } from 'file-saver';
import {
    generateDownload,
    getGenerateProgress,
    getTaskDetail,
    getTaskDownload,
} from '../../../utils/net';
import {
    cardsTypeAtom,
    cardsTypeConst,
    downloadProgressAtom,
    isPurchasedAtom,
    lastGenerateUUIDAtom,
    logInfoAtom,
    regenePrivateSetAtom,
    showDownloadAtom,
    showLoginAtom,
    showRegenerateAtom,
    taskDetailAtom,
    packInfoAtom,
    progressCheckingQueueAtom,
    userPermissionAtom,
    openPaymentInfoAtom,
    modalObjAtom,
    subscriptionsInfoAtom,
    balanceAtom,
} from '../../../store';
import style from './bundlePanel.module.css';
import { useTips } from '../../../common/GlobalTips';
import walletIcon from '../../../assets/wallet.png';
import groupWalletIcon from '../../../assets/groupWallet.png';
import finishIconSrc from '../../../assets/finish.png';
import { ModalClickContext, PermissionContext } from '../../../utils/context';
import { qualityArr, topologyArr, AdditionalArr } from '../../../utils/map'
import { trackEvent } from '../../../utils/trackEvent'
import { useRefreshInfo } from '../../../utils/hooks';
import { driver } from 'driver.js';
import { Plugin } from '../../../common/PlugIn';
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { Turnstile } from "@marsidev/react-turnstile";
import { handleCaptchaVerification } from "../../../utils/auth";


const BundlePanel = React.forwardRef((props, ref) => {
    const balance = useRecoilValue(balanceAtom);
    const [openPaymentInfo, setOpenPaymentInfo] = useRecoilState(openPaymentInfoAtom)
    const lastGenerateUUID = useRecoilValue(lastGenerateUUIDAtom);
    const userPermission = useRecoilValue(userPermissionAtom)
    const handleModalClick = useContext(ModalClickContext);
    const setRegenePrivate = useSetRecoilState(regenePrivateSetAtom);
    const [downloadStage, setDownloadStage] = useState(2);
    const [qualityIndex, setQualityIndex] = useState(0);
    const [topologyIndex, setTopologyIndex] = useState(0);
    const [repackTotalCost, setRepackTotalCost] = useState("--")
    const [payBtn, setPayBtn] = useState('NEXT0')
    const [additional, setAdditional] = useState([]);
    const [bodyOpt, setBodyOpt] = useState({ gender: 2 });
    const taskDetail = useRecoilValue(taskDetailAtom);
    const logInfo = useRecoilValue(logInfoAtom);
    const [modalObj, setModalObj] = useRecoilState(modalObjAtom);
    const [packInfo, setPackInfo] = useRecoilState(packInfoAtom);
    const [downloadProgress, setDownloadProgress] = useRecoilState(downloadProgressAtom);
    const [showDownload,] = useRecoilState(showDownloadAtom);
    const setShowLogin = useSetRecoilState(showLoginAtom);
    const setShowRegenerate = useSetRecoilState(showRegenerateAtom);
    const setIsPurchased = useSetRecoilState(isPurchasedAtom);
    const setCardType = useSetRecoilState(cardsTypeAtom);
    const tip = useTips();
    const [autoParams, setAutoParams] = useState(0);
    const [repack, setRepack] = useState(false);
    const progressRef = useRef({});
    const { refreshInfo } = useRefreshInfo()
    const [finish, setFinish] = useState(false)
    const { t } = useTranslation();
    const progressCheckingQueue = useRecoilValue(progressCheckingQueueAtom);
    const { isAllowedTo } = useContext(PermissionContext);
    const base_addtionalArr = taskDetail?.settings?.caption_style ? AdditionalArr.filter((i) => i.index === 3) : AdditionalArr
    const extra_addtionalArr = userPermission.chatavatar_allowed_additional_process
    const extra_topology = taskDetail?.group?.settings?.allowed_topology.filter((item) => item !== "Daz3d")
    const subscriptionsInfo = useRecoilValue(subscriptionsInfoAtom);
    const turnstileRef = useRef()

    const zip = useRef(null);

    const loadJSZip = useCallback(async () => {
        if (!zip.current) {
            try {
                const JSZipModule = await import('jszip');
                zip.current = new JSZipModule.default();
            } catch (error) {
                console.error('Error loading JSZip:', error);
                throw error;
            }
        }
        return zip.current;
    }, []);

    useEffect(() => {
        const loadZip = async () => {
            zip.current = await loadJSZip();
        }

        loadZip();
    }, []);


    useEffect(() => {
        getGenerateProgress(taskDetail.task_uuid).then((data) => {
            const res = data.data;
            if (res.stage !== 'Done') {
                setPackInfo({
                    ...packInfo,
                    isPaid: true,
                    isPackFinished: false
                })
            } else if (
                res.stage === 'Done' && ['DoneBasic', 'DoneHigh'].includes(packInfo.details.user_state)
            ) {
                setPackInfo({
                    ...packInfo,
                    isPaid: true,
                    isPackFinished: true
                })
            } else {
                setPackInfo({
                    ...packInfo,
                    isPaid: false,
                })
            }
        }
        );
        return () => {
            setTopologyIndex(0);
            setAdditional([]);
            setQualityIndex(0);
            setDownloadStage(2);
            setRepack(false)
        };

    }, []);

    useEffect(() => {
        if (packInfo.isPaid && !progressCheckingQueue.some((item) => item.task_uuid === taskDetail.task_uuid)) {
            setPackInfo({
                ...packInfo,
                isPaid: true,
                isPackFinished: true
            })
        }
    }, [progressCheckingQueue])

    useEffect(() => {
        if (!showDownload) {
            setTopologyIndex(0);
            setAdditional([]);
            setQualityIndex(0);
            setDownloadStage(2);
        }
    }, [showDownload])

    useEffect(() => {
        if (topologyIndex >= topologyArr.length - 1) {
            setAdditional([])
        }
    }, [topologyIndex])

    useEffect(() => {
        if (finish) {
            const zipFile = async () => {
                const content = await zip.current.generateAsync({ type: 'blob' });
                const timestamp = Date.now();
                saveAs(content, `${timestamp}.zip`);
            }
            zipFile()
            setProgress(1)
        }
    }, [finish])

    useEffect(() => {
        if (showDownload && repack && payBtn === 'PAY') {
            testCoinOrPack(true)
        }
    }, [payBtn, additional])

    const testCoinOrPack = async (test) => {
        try {
            const req = {
                task_uuid: taskDetail.task_uuid,
                resolution: qualityArr[qualityIndex].resolution,
                topology: topologyIndex > topologyArr.length - 1 ? extra_topology[topologyIndex - topologyArr.length] : topologyArr[topologyIndex].topology,
            };
            if (test) {
                req.is_test = true
            }
            if (additional.length) {
                req.additional = additional.map((opt) => AdditionalArr[opt].option);
            }

            if (topologyIndex === 0 && additional.includes(0)) {
                req.settings = {
                    height: Number(bodyOpt.height),
                    weight: Number(bodyOpt.weight),
                    gender: bodyOpt.gender === 1,
                };
            }
            trackEvent('ChatAvatar - Card - Download: Pay', req, taskDetail.task_uuid)
            const captchaToken = await handleCaptchaVerification(turnstileRef, isAllowedTo('captcha.feature'))

            const res = await generateDownload(req, captchaToken);
            if (!res?.data?.error && repack) {
                let result = res?.data?.total_price
                if (isNaN(result)) {
                    setRepackTotalCost("--")
                } else {
                    setRepackTotalCost(result / 10)
                }
            }
        } catch (e) {
            Sentry.captureException(e)
            console.error(e);
        }
    }

    const driverGuide = (balance) => {
        setModalObj({ ...modalObj, state: false });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setOpenPaymentInfo({
            ...openPaymentInfo,
            state: true,
            group: taskDetail?.group,
            balance: balance,
            step: 1
        })

        const driverObj = new driver({
            overlayColor: 'rgba(0,0,0,0.6)',
        });
        const target = taskDetail?.group ? "#rechargeGroup #rechargePopup" : "#rechargeSelf #rechargePopup"
        driverObj.highlight({
            element: target,
            popover: {
                title: t('DRIVEROBJ_BALANCE_TITLE'),
                description: t('DRIVEROBJ_BALANCE_DESC'),
                popoverClass: "rechargedriver"
            }
        });
        if (document.querySelector(target)) {
            document.querySelector(target).addEventListener("click", () => {
                driverObj.destroy();
            });
        }
    }

    const handlePay = async () => {
        if (!logInfo) {
            setShowLogin(true);
            return;
        }

        if ((repack && (balance.selfBalance + subscriptionsInfo.wallet_balance) / 10 < repackTotalCost) || (!repack && (balance.selfBalance + subscriptionsInfo.wallet_balance) / 10 < totalCost)) {
            if (repack) {
                driverGuide(repackTotalCost - (balance.selfBalance + subscriptionsInfo.wallet_balance) / 10)
            } else {
                driverGuide(totalCost - (balance.selfBalance + subscriptionsInfo.wallet_balance) / 10)
            }
            return;
        }
        try {
            await testCoinOrPack()
            const response = await getTaskDetail(taskDetail.task_uuid);
            if (response?.data?.user_state === 'DonePreview') {
                //pack 如果有身体部分的话 user_state会变成Donepreview  处理这个特殊情况
                response.data.user_state = 'DoneBasic'
            }
            setPackInfo({
                ...packInfo,
                isPaid: true,
                details: response.data
            })
            if (lastGenerateUUID === taskDetail.task_uuid) {
                setShowRegenerate(false);
                setRegenePrivate({
                    oldPrivate: '',
                    currentPrivate: '',
                    privatedChange: '',
                });
                // setLastGenerateUUID(false)
            }
            await props.onLikeChange(null, true)
            setIsPurchased(true)
            refreshInfo()
        } catch (e) {
            Sentry.captureException(e)
        }
    };

    async function fetchWithRetries(url, onProgress, retries = 3) {
        for (let i = 0; i < retries; i++) {
            try {
                return await fetchWithProgress(url, onProgress);
            } catch (error) {
                Sentry.captureException(error)
                if (i === retries - 1) { // 如果是最后一次尝试仍然失败，则抛出异常
                    throw error;
                }
                // 如果不是最后一次尝试，则等待一段时间再试
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    }

    async function fetchWithProgress(url, onProgress) {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.status}`);
        }

        const reader = response.body.getReader();
        const contentLength = +response.headers.get('Content-Length');
        let receivedLength = 0;

        const chunks = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            chunks.push(value);
            receivedLength += value.length;
            onProgress && onProgress(receivedLength / contentLength);
        }

        const chunksAll = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
        }
        return new Blob([chunksAll]);
    }

    const handleDownload = async () => {
        setProgress(0.01)
        try {
            // const response = await getTaskDetail(taskDetail.task_uuid);
            const urls = (await getTaskDownload(taskDetail.task_uuid))?.list
            if (!urls) {
                throw new Error("Resource download error.")
            }
            if (modalObj.type !== "ImagineFace") {
                zip.current.file('prompt.txt', taskDetail.prompt);
            }

            const err = urls.find((url) => url.error);
            if (err) {
                throw new Error(err.error);
            }

            const totalFiles = Math.max(urls.length, 1);

            const responses = await Promise.all(
                urls.map(async (urlObj) => {
                    const { url, name } = urlObj;
                    if (!url || url === 'FILE_NOT_FOUND') {
                        tip({
                            type: 'info',
                            content: `${name} not found`,
                        });
                        return null;
                    }
                    const blob = await fetchWithRetries(url, (progress) => {
                        progress = Math.min(progress, 1);
                        progressRef.current[`${name}`] = progress;
                        const totalProgress = parseFloat(
                            Object.values(progressRef.current)
                                .reduce((sum, p) => sum + p / totalFiles, 0)
                                .toFixed(2),
                        );
                        setProgress(totalProgress)

                    });
                    return { blob, name };
                }),
            );
            for (const responseObj of responses) {
                if (!responseObj) continue;
                const { blob, name } = responseObj;
                zip.current.file(name, blob);
            }
            setFinish(true)
            progressRef.current = {};
        } catch (e) {
            Sentry.captureException(e)
            deletProgress()
        }
    };

    const getProgress = () => downloadProgress.get(taskDetail.task_uuid)

    const setProgress = (num) => {
        const newProgress = new Map(JSON.parse(JSON.stringify(Array.from(downloadProgress))));
        newProgress.set(taskDetail.task_uuid, num);
        setDownloadProgress(newProgress);
        if (num === 1) {
            setTimeout(() => {
                deletProgress()
            }, 3000);
        }
    }

    const deletProgress = () => {
        const newProgress = new Map(JSON.parse(JSON.stringify(Array.from(downloadProgress))));
        newProgress.delete(taskDetail.task_uuid);
        setDownloadProgress(newProgress);
    }

    const handleRepack = () => {
        setTopologyIndex(0);
        setAdditional([]);
        setQualityIndex(0);
        setDownloadStage(2);
        setRepack(true)
        setPackInfo({
            ...packInfo,
            isPaid: false,
            isPackFinished: false
        })
    };

    const handleBack = () => {
        if (!logInfo) {
            setShowLogin(true);
            return;
        }
        switch (downloadStage) {
            case 0:
                setDownloadStage(2);
                break;
            case 1:
                setDownloadStage(0);
                break;
            case 2:
                setPackInfo({
                    ...packInfo,
                    isPaid: true,
                    isPackFinished: true
                });
                break;
        }
    };

    const handleToMine = () => {
        handleModalClick();
        setCardType(cardsTypeConst.Mine);
    };

    const handleAdditionalClick = (opt) => () => {
        additional.includes(opt)
            ? setAdditional(additional.filter((cur) => cur !== opt))
            : setAdditional([...additional, opt]);
    };

    useEffect(() => {
        const btnTextMap = {
            1: 'PAY',
            2: ['us-comic', 'cartoon'].includes(taskDetail?.settings?.style) ? 'PAY' : "NEXT0",
            0: (topologyIndex === 0 || (topologyIndex > topologyArr.length - 1 && extra_addtionalArr)) ? "NEXT1" : 'PAY'
        };
        setPayBtn(btnTextMap[downloadStage]);
    }, [topologyIndex, extra_addtionalArr, downloadStage])

    const handlerNext = (target) => {
        setDownloadStage(target)
    }

    const payBtnComponent = {
        'PAY': <div className={style.downloadBtn} onClick={handlePay}>{t('BUTTON_PAY')}</div>,
        "NEXT0": <div className={style.downloadBtn} onClick={handlerNext.bind(null, 0)}>{t('BUTTON_NEXT')}</div>,
        "NEXT1": <div className={style.downloadBtn} onClick={handlerNext.bind(null, 1)}>{t('BUTTON_NEXT')}</div>,
    }

    const totalCost = qualityArr[qualityIndex].credits + topologyArr[topologyIndex >= topologyArr.length ? 1 : topologyIndex].credits + additional.length * 1

    const handlerOpencowtransfer = () => {
        window.open(
            Intl.DateTimeFormat().resolvedOptions().timeZone
                === 'Asia/Shanghai'
                ? 'https://cowtransfer.com/s/1994387abbd74f'
                : 'https://drive.google.com/drive/folders/1kyhe5ZI40yrGyyNfzboTF_6cU539p10m?usp=share_link',
            '_blank',
        )
    }

    const handlersetTopologyIndex = (index) => setTopologyIndex(index)

    const handlerCheckBodyOpt = (index) => setBodyOpt({ ...bodyOpt, gender: index })

    const handlerToggleAuto = (index) => {
        if (index === 0) {
            setBodyOpt({ ...bodyOpt, weight: null, height: null });
        }
        setAutoParams(index);
    }

    const handlerSetQualityIndex = (index) => setQualityIndex(index)

    return (
        <div className={style.downloadCon} ref={ref}>
            {packInfo.isPaid === null ? (
                <div className={style.loading}>Loading...</div>
            ) : packInfo.isPaid ? (
                <>
                    {!packInfo.isPackFinished ? (
                        <div className={style.paidCon}>
                            <img className={style.paidIcon} src={finishIconSrc} alt="finish" />
                            <div className={style.paidTitle}>{t('CHATAVATAR_GENERTAE_BOARD_PAID_CON_TIPS_1')}!</div>
                            <div className={style.paidSubtitle}>
                                {t('CHATAVATAR_GENERTAE_BOARD_PAID_CON_TIPS_2')}
                                {' '}
                                <span className={style.clickable} onPointerDown={handleToMine}>
                                    {t('CHATAVATAR_GENERTAE_BOARD_PAID_CON_TIPS_3')}
                                </span>
                            </div>
                            <div className={style.paidProgress}>
                                {t('CHATAVATAR_GENERTAE_BOARD_PAID_CON_TIPS_4')}...
                            </div>
                            <Plugin type="pack" />
                        </div>
                    ) : (
                        <div className={style.paidCon}>
                            <img className={style.paidIcon} src={finishIconSrc} alt="finish" />
                            <div className={style.paidTitle}>
                                {getProgress() === undefined
                                    ? 'Pack finished!'
                                    : getProgress() === 1
                                        ? 'Download finished!'
                                        : 'Downloading...'}
                            </div>
                            <div className={style.paidSubtitle}>
                                <span
                                    className={style.clickable}
                                    onClick={handlerOpencowtransfer}
                                >
                                    {t('CHATAVATAR_GENERTAE_BOARD_PAID_SUB_TIPS_1')}
                                    {' '}
                                </span>
                                {t('CHATAVATAR_GENERTAE_BOARD_PAID_SUB_TIPS_2')}
                            </div>
                            {getProgress() >= 0 ? (
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <div
                                        style={{
                                            zIndex: 2,
                                            color: 'var(--theme-color)',
                                            position: 'absolute',
                                            bottom: '.5rem',
                                            fontSize: '.8rem',
                                            left: `${getProgress() * 100 <= 10
                                                ? 1
                                                : getProgress() * 100 >= 85
                                                    ? 85
                                                    : getProgress() * 100
                                                }%`,
                                        }}
                                    >
                                        {parseInt(parseFloat(getProgress()).toFixed(2) * 100)}
                                        %
                                    </div>
                                    <div className={style.progressCon}>
                                        <div
                                            className={style.progressBar}
                                            style={{ width: `${getProgress() * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={style.downloadBtns}>
                                    <div className={style.downloadBtn} onPointerDown={handleDownload}>
                                        {t('BUTTON_DOWNLOAD')}
                                    </div>
                                    <div className={`${style.downloadBtn} ${style.repack}`} onPointerDown={handleRepack}>
                                        {t('BUTTON_REPACK')}
                                    </div>
                                </div>
                            )}
                            <Plugin type="pack" />
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className={style.downloadTitle}>
                        <div>{t('CHATAVATAR_GENERTAE_BOARD_TITLE_1')}</div>
                        {downloadStage !== 2 || (downloadStage === 2 && repack) ? (
                            <div className={style.backICon} onPointerDown={handleBack}>
                                {'< '}
                                {t('CHATAVATAR_GENERTAE_BOARD_TITLE_2')}
                            </div>
                        ) : null}
                    </div>
                    {downloadStage === 0 && (
                        <>
                            {(extra_topology ? [...topologyArr, ...extra_topology.map((item) => {
                                return {
                                    name: item,
                                    topology: 'MetaHuman',
                                    subscript: 'Group',
                                    credits: 4
                                }
                            })] : topologyArr).map((item, index) => {
                                return <div
                                    key={index + item.name}
                                    className={`${style.row} ${topologyIndex === index ? style.selected : ''}`}
                                    onClick={handlersetTopologyIndex.bind(null, index)}
                                >
                                    <div className={`${style.priceCon}  ${index > topologyArr.length - 1 ? style.groupIcon : ''}`}>
                                        <img className={style.icon} src={index > topologyArr.length - 1 ? groupWalletIcon : walletIcon} alt="wallet" />
                                        <div className={style.price}>{item.credits}</div>
                                    </div>
                                    <div className={style.option}>{item.name === "PerfectWorld" ? "PNW-fat" : item.name}</div>
                                    {index === 0 && <div className={`${style.corner} ${style.sec}`}>{item.subscript}</div>}
                                    {index > topologyArr.length - 1 && <div className={`${style.corner} ${style.groupMeta}`}>{item.subscript}</div>}
                                </div>
                            })}

                        </>
                    )}
                    {downloadStage === 1 && (
                        <div style={{ overflowY: 'auto', maxHeight: '24rem' }}>
                            {((topologyIndex > topologyArr.length - 1 && extra_addtionalArr && extra_topology) ? base_addtionalArr.filter((item) => extra_addtionalArr[extra_topology[topologyIndex - topologyArr.length]].includes(item.option)) : base_addtionalArr).map((item) => {
                                return <React.Fragment
                                    key={item.option}>
                                    <div
                                        className={`${style.row} ${additional.includes(base_addtionalArr.find(itm => itm.option === item.option).index) ? style.selected : ''}`}
                                        onPointerDown={handleAdditionalClick(base_addtionalArr.find(itm => itm.option === item.option).index)}
                                    >
                                        <div className={style.priceCon}>
                                            <img className={style.icon} src={walletIcon} alt="wallet" />
                                            <div className={style.price}>{item.credits}</div>
                                        </div>
                                        <div className={style.option}>{item.name}</div>

                                    </div>
                                    {
                                        base_addtionalArr.find(itm => itm.option === item.option).index === 0 &&
                                        <div
                                            className={`${style.bodyOptCon} ${additional.includes(0) ? style.show : style.hide
                                                }`}>
                                            <div className={style.subRow}>
                                                {['Male', 'Female', 'Others'].map((item, index) => {
                                                    return <div
                                                        key={item}
                                                        className={`${style.genderBtn} ${bodyOpt.gender === index ? style.selected : ''
                                                            }`}
                                                        onClick={handlerCheckBodyOpt.bind(null, index)}
                                                    >
                                                        {item}
                                                    </div>
                                                })}
                                            </div>
                                            <div className={style.subRow}>
                                                {['Auto', 'Manual'].map((item, index) => {
                                                    return <div
                                                        key={item}
                                                        className={`${style.genderBtn} ${autoParams === index ? style.selected : ''
                                                            }`}
                                                        onClick={handlerToggleAuto.bind(null, index)}>
                                                        {item}
                                                    </div>
                                                })}
                                            </div>
                                            <div>
                                                <div className={style.subRow}>
                                                    <div className={style.label}>Height</div>

                                                    <div className={style.inputCon}>
                                                        <input
                                                            type="range"
                                                            min="140"
                                                            max="220"
                                                            value={bodyOpt.height}
                                                            onChange={(ev) => setBodyOpt({
                                                                ...bodyOpt,
                                                                height: ev.currentTarget.value,
                                                            })}
                                                            className={style.slider}
                                                            disabled={autoParams === 0}
                                                        />
                                                        <div style={{ width: '4.4rem', fontSize: '14.5px' }}>
                                                            <span className={style.suffixHeight}>
                                                                {autoParams === 1
                                                                    ? bodyOpt.height
                                                                        ? `${bodyOpt.height} cm`
                                                                        : ' cm'
                                                                    : 'Auto'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={style.subRow}>
                                                    <div className={style.label}>Weight</div>

                                                    <div className={style.inputCon}>
                                                        <input
                                                            type="range"
                                                            min="40"
                                                            max="110"
                                                            value={bodyOpt.weight}
                                                            onChange={(ev) => setBodyOpt({
                                                                ...bodyOpt,
                                                                weight: ev.currentTarget.value,
                                                            })}
                                                            className={style.slider}
                                                            disabled={autoParams === 0}
                                                        />
                                                        <div style={{ width: '4.4rem', fontSize: '14.5px' }}>
                                                            <span className={style.suffixWeight}>
                                                                {autoParams === 1
                                                                    ? bodyOpt.weight
                                                                        ? `${bodyOpt.weight} kg`
                                                                        : ' kg'
                                                                    : 'Auto'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </React.Fragment>
                            })}
                        </div>
                    )}
                    {downloadStage === 2 && (
                        <>
                            {qualityArr.map((item, index) => {
                                return <div
                                    key={index + item.name}
                                    className={`${style.row} ${qualityIndex === index ? style.selected : ''
                                        }`}
                                    onClick={handlerSetQualityIndex.bind(null, index)}
                                >
                                    <div className={style.priceCon}>
                                        <img className={style.icon} src={walletIcon} alt="wallet" />
                                        <div className={style.price}>{item.credits}</div>
                                    </div>
                                    <div className={style.option}>{item.name}</div>
                                </div>
                            })}
                        </>
                    )}
                    <div className={style.costCon}>
                        Payment amount:
                        <span style={{
                            textDecoration: payBtn === 'PAY' && repack ? 'line-through  red' : ''
                        }}>&nbsp;{totalCost}</span>
                        {payBtn === 'PAY' && repack && <span style={{
                            color: 'red'
                        }}>&nbsp;{repackTotalCost}</span>}
                    </div>
                    {payBtnComponent[payBtn]}
                </>
            )
            }
            <Turnstile ref={turnstileRef} siteKey='0x4AAAAAAAhbdFXzdFzkoDOV'
                style={{ display: "none" }}
                options={{ appearance: 'interaction-only', execution: 'execute' }} />
        </div>
    );
});

BundlePanel.displayName = "BundlePanel"
export { BundlePanel };
